import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { resellerService, customerService, branchService } from '../services/api';
import { toast } from 'react-toastify';
import Button from '../components/Button';
import {
  FiArrowLeft, FiDownload, FiSearch, FiPrinter, FiUsers,
  FiBriefcase, FiCalendar, FiPackage, FiClock, FiShield,
} from 'react-icons/fi';

const fmtDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return '—';
  return dt.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
};

const daysBetween = (a, b) => Math.ceil((new Date(a).getTime() - new Date(b).getTime()) / (1000 * 60 * 60 * 24));

// Colour pill for subscription status. Same palette as Resellers.jsx.
const pillFor = (status) => {
  const s = String(status || '').toLowerCase();
  if (s === 'active')    return 'bg-green-100 text-green-700';
  if (s === 'trial')     return 'bg-blue-100 text-blue-700';
  if (s === 'expired')   return 'bg-red-100 text-red-700';
  if (s === 'cancelled') return 'bg-gray-200 text-gray-700';
  return 'bg-gray-100 text-gray-600';
};

// Escape one CSV cell — wrap in quotes if it contains a comma, quote,
// or newline; double any embedded quotes per RFC 4180.
const csvCell = (v) => {
  if (v === null || v === undefined) return '';
  const s = String(v);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

const ResellerMerchants = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [reseller, setReseller] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [rRes, cRes, bRes] = await Promise.all([
          resellerService.getOne(id),
          customerService.getAll({ resellerId: id, limit: 500 }),
          branchService.getByReseller(id),
        ]);
        if (cancelled) return;
        setReseller(rRes.data?.data || null);

        const branches = bRes.data?.data || [];
        const branchCountByCustomer = {};
        branches.forEach(b => {
          const cid = b.customerId?._id || b.customerId;
          if (cid) branchCountByCustomer[cid] = (branchCountByCustomer[cid] || 0) + 1;
        });

        const customers = (cRes.data?.data || []).map(c => ({
          ...c,
          _branchCount: branchCountByCustomer[c._id] || 0,
        }));
        setRows(customers);
      } catch (err) {
        toast.error(err?.response?.data?.message || 'Failed to load merchants');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return rows.filter(r => {
      if (statusFilter !== 'all' && (r.subscriptionStatus || '').toLowerCase() !== statusFilter) return false;
      if (!s) return true;
      return (
        (r.businessName || '').toLowerCase().includes(s) ||
        (r.businessEmail || '').toLowerCase().includes(s) ||
        (r.businessPhone || '').includes(s) ||
        (r.owner?.name || '').toLowerCase().includes(s) ||
        (r.planId?.planName || '').toLowerCase().includes(s)
      );
    });
  }, [rows, q, statusFilter]);

  // Summary tiles above the table.
  const summary = useMemo(() => {
    const total = rows.length;
    const active = rows.filter(r => (r.subscriptionStatus || '').toLowerCase() === 'active').length;
    const trial  = rows.filter(r => (r.subscriptionStatus || '').toLowerCase() === 'trial').length;
    const expired = rows.filter(r => (r.subscriptionStatus || '').toLowerCase() === 'expired').length;
    return { total, active, trial, expired };
  }, [rows]);

  const handleExport = () => {
    if (filtered.length === 0) { toast.warning('No merchants to export'); return; }
    const header = [
      'Business Name', 'Business Email', 'Business Phone', 'Owner Name', 'Owner Email',
      'Plan', 'Subscription Status', 'Joined Date', 'Trial Ends', 'Subscription Ends',
      'Billing Cycle', 'Billing Amount', 'Next Billing', 'Branches', 'Max Branches',
      'Max Users', 'Max Products', 'Active', 'Suspended', 'Suspend Reason',
    ];
    const lines = [header.join(',')];
    filtered.forEach(c => {
      lines.push([
        c.businessName, c.businessEmail, c.businessPhone,
        c.owner?.name, c.owner?.email,
        c.planId?.planName, c.subscriptionStatus,
        fmtDate(c.createdAt), fmtDate(c.trialEndsAt), fmtDate(c.subscriptionEndsAt),
        c.billingCycle, c.billingAmount, fmtDate(c.nextBillingDate),
        c._branchCount, c.currentPlan?.maxBranches, c.currentPlan?.maxUsers, c.currentPlan?.maxProducts,
        c.isActive ? 'Yes' : 'No', c.isSuspended ? 'Yes' : 'No', c.suspendedReason || '',
      ].map(csvCell).join(','));
    });
    const blob = new Blob(['﻿' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const safeName = (reseller?.companyName || 'reseller').replace(/[^\w-]+/g, '_');
    a.download = `merchants_${safeName}_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 3000);
    toast.success(`Exported ${filtered.length} merchant(s)`);
  };

  return (
    <div className="space-y-4 print:space-y-2">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap print:hidden">
        <div>
          <button
            onClick={() => navigate('/resellers')}
            className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 mb-1"
          >
            <FiArrowLeft size={12} /> Back to Resellers
          </button>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <FiUsers /> Merchants under {reseller?.companyName || '…'}
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {reseller?.companyEmail} · Registered {fmtDate(reseller?.createdAt)}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => window.print()}>
            <FiPrinter size={14} /> Print
          </Button>
          <Button onClick={handleExport}>
            <FiDownload size={14} /> Export CSV
          </Button>
        </div>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 print:grid-cols-4">
        <div className="p-3 bg-white rounded-lg border">
          <div className="text-[10px] text-gray-500 uppercase font-semibold flex items-center gap-1">
            <FiBriefcase size={10} /> Total
          </div>
          <div className="text-2xl font-bold text-gray-900 mt-0.5">{summary.total}</div>
        </div>
        <div className="p-3 bg-white rounded-lg border">
          <div className="text-[10px] text-green-600 uppercase font-semibold">Active</div>
          <div className="text-2xl font-bold text-green-700 mt-0.5">{summary.active}</div>
        </div>
        <div className="p-3 bg-white rounded-lg border">
          <div className="text-[10px] text-blue-600 uppercase font-semibold">Trial</div>
          <div className="text-2xl font-bold text-blue-700 mt-0.5">{summary.trial}</div>
        </div>
        <div className="p-3 bg-white rounded-lg border">
          <div className="text-[10px] text-red-600 uppercase font-semibold">Expired</div>
          <div className="text-2xl font-bold text-red-700 mt-0.5">{summary.expired}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border p-3 flex flex-col sm:flex-row gap-2 print:hidden">
        <div className="relative flex-1">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={13} />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, email, phone, plan..."
            className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-200"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white sm:w-48"
        >
          <option value="all">All statuses</option>
          <option value="active">Active</option>
          <option value="trial">Trial</option>
          <option value="expired">Expired</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-40"><div className="spinner" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <FiUsers size={32} className="mx-auto mb-2 opacity-40" />
            <p className="text-sm font-medium">No merchants match your filters</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr className="text-left text-[11px] text-gray-500 uppercase tracking-wide">
                  <th className="px-3 py-2.5">#</th>
                  <th className="px-3 py-2.5">Merchant</th>
                  <th className="px-3 py-2.5">Owner</th>
                  <th className="px-3 py-2.5">Plan</th>
                  <th className="px-3 py-2.5">Status</th>
                  <th className="px-3 py-2.5">Joined</th>
                  <th className="px-3 py-2.5">Expires</th>
                  <th className="px-3 py-2.5 text-right">Branches</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((c, idx) => {
                  const endsAt = c.subscriptionEndsAt || c.trialEndsAt;
                  const days = endsAt ? daysBetween(endsAt, new Date()) : null;
                  const daysBadge = days === null ? null
                    : days < 0 ? <span className="text-[10px] text-red-600 font-semibold">expired {Math.abs(days)}d ago</span>
                    : days <= 14 ? <span className="text-[10px] text-amber-600 font-semibold">in {days}d</span>
                    : <span className="text-[10px] text-gray-500">in {days}d</span>;
                  return (
                    <tr key={c._id} className="hover:bg-gray-50">
                      <td className="px-3 py-2.5 text-xs text-gray-400 font-mono">{idx + 1}</td>
                      <td className="px-3 py-2.5">
                        <div className="font-semibold text-gray-800">{c.businessName}</div>
                        <div className="text-xs text-gray-500">{c.businessEmail}</div>
                        {c.businessPhone && <div className="text-xs text-gray-400">{c.businessPhone}</div>}
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="text-xs text-gray-700">{c.owner?.name || '—'}</div>
                        {c.owner?.email && <div className="text-[11px] text-gray-500">{c.owner.email}</div>}
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="inline-flex items-center gap-1 text-xs font-medium text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded">
                          <FiPackage size={11} /> {c.planId?.planName || '—'}
                        </div>
                        {c.billingCycle && (
                          <div className="text-[10px] text-gray-500 mt-1 capitalize">{c.billingCycle}</div>
                        )}
                      </td>
                      <td className="px-3 py-2.5">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold capitalize ${pillFor(c.subscriptionStatus)}`}>
                          {c.subscriptionStatus || 'unknown'}
                        </span>
                        {c.isSuspended && (
                          <div className="text-[10px] text-red-500 mt-1">Suspended</div>
                        )}
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="text-xs text-gray-700 flex items-center gap-1">
                          <FiCalendar size={11} className="text-gray-400" /> {fmtDate(c.createdAt)}
                        </div>
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="text-xs text-gray-700 flex items-center gap-1">
                          <FiClock size={11} className="text-gray-400" /> {fmtDate(endsAt)}
                        </div>
                        {daysBadge && <div className="mt-0.5">{daysBadge}</div>}
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        <span className="text-sm font-semibold text-gray-800">{c._branchCount}</span>
                        {c.currentPlan?.maxBranches && (
                          <span className="text-[10px] text-gray-400"> / {c.currentPlan.maxBranches}</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Footer summary */}
      {!loading && filtered.length > 0 && (
        <p className="text-xs text-gray-500 text-center">
          Showing {filtered.length} of {rows.length} merchants
        </p>
      )}
    </div>
  );
};

export default ResellerMerchants;
